import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, LoadScript, Marker, InfoWindow } from "@react-google-maps/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  MdRefresh,
  MdSearch,
  MdClose,
  MdDeleteOutline,
  MdVisibility,
  MdMap,
  MdChevronLeft,
  MdChevronRight,
  MdLocalPharmacy,
  MdPerson,
  MdTwoWheeler,
  MdFiberManualRecord,
  MdReceiptLong,
  MdInventory2,
  MdPayments,
} from "react-icons/md";
import Card from "components/card";
import { ldAdminApi } from "./api";

import { GOOGLE_MAPS_API_KEY as GOOGLE_KEY } from "utils/googleMaps";

const STATES = [
  "PENDING", "LINK_SENT", "EXPIRED", "LOCATION_CONFIRMED", "PAYMENT_DONE",
  "SEARCHING_RIDER", "NO_RIDERS_AVAILABLE", "RIDER_ASSIGNED",
  "ARRIVED_AT_STORE", "PICKED_UP", "RIDER_CANCELLED", "OUT_FOR_DELIVERY",
  "EXTRA_DISTANCE_REQUESTED", "DELIVERED", "CANCELLED",
];

const PAGE_SIZES = [10, 25, 50, 100];

// status -> colour family (chip + dot styling)
const STATUS_TONE = {
  PENDING: "amber", LINK_SENT: "amber", LOCATION_CONFIRMED: "amber",
  SEARCHING_RIDER: "amber", EXTRA_DISTANCE_REQUESTED: "amber",
  PAYMENT_DONE: "blue", RIDER_ASSIGNED: "blue", ARRIVED_AT_STORE: "blue",
  PICKED_UP: "blue", OUT_FOR_DELIVERY: "blue",
  DELIVERED: "green",
  EXPIRED: "red", NO_RIDERS_AVAILABLE: "red", RIDER_CANCELLED: "red", CANCELLED: "red",
};

const TONE_CLASS = {
  amber: "bg-amber-50 text-amber-700",
  blue: "bg-blue-50 text-blue-700",
  green: "bg-green-50 text-green-700",
  red: "bg-red-50 text-red-600",
  gray: "bg-gray-100 text-gray-600",
};
const DOT_CLASS = {
  amber: "text-amber-500", blue: "text-blue-500", green: "text-green-500",
  red: "text-red-500", gray: "text-gray-400",
};
const DOT_BG = {
  amber: "bg-amber-400", blue: "bg-blue-500", green: "bg-green-500",
  red: "bg-red-500", gray: "bg-gray-300",
};
// soft gradient band behind the modal header, tinted by status
const HERO_BG = {
  amber: "bg-gradient-to-br from-amber-50 to-white dark:from-amber-500/10 dark:to-navy-800",
  blue: "bg-gradient-to-br from-blue-50 to-white dark:from-blue-500/10 dark:to-navy-800",
  green: "bg-gradient-to-br from-green-50 to-white dark:from-green-500/10 dark:to-navy-800",
  red: "bg-gradient-to-br from-red-50 to-white dark:from-red-500/10 dark:to-navy-800",
  gray: "bg-gradient-to-br from-gray-50 to-white dark:from-white/5 dark:to-navy-800",
};
const toneOf = (s) => STATUS_TONE[s] || "gray";
// "OUT_FOR_DELIVERY" -> "OUT FOR DELIVERY" for display (raw value kept for filtering)
const prettyStatus = (s) => (s || "").replace(/_/g, " ");

export default function LiveOps() {
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState({ bookings: [], breakdown: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selected, setSelected] = useState(null); // { id, tab }
  const [deletingId, setDeletingId] = useState(null);
  const firstLoad = useRef(true);

  const load = async () => {
    if (firstLoad.current) setLoading(true);
    try {
      const res = await ldAdminApi.liveOps({
        ...(statusFilter ? { status: statusFilter } : {}),
        page,
        limit: pageSize,
      });
      setData(res);
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
      firstLoad.current = false;
    }
  };

  useEffect(() => {
    firstLoad.current = true;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page, pageSize]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, statusFilter, page, pageSize]);

  // reset to page 1 when filter/size change
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, pageSize]);

  const total = data.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const allBreakdown = useMemo(
    () => (data.breakdown || []).reduce((s, b) => s + b.count, 0),
    [data.breakdown]
  );

  const rows = useMemo(() => {
    const list = data.bookings || [];
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((b) =>
      [b.shortToken, b.pharmacyName, b.pharmacyId, b.customerMobile, b.customerName, b.riderName]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [data.bookings, search]);

  const remove = async (b) => {
    if (!window.confirm(`Delete booking ${b.shortToken}? This permanently removes it.`)) return;
    setDeletingId(b._id);
    try {
      await ldAdminApi.deleteBooking(b._id);
      toast.success("Booking deleted");
      if (selected?.id === b._id) setSelected(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || e.message);
    } finally {
      setDeletingId(null);
    }
  };

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  return (
    <div className="mt-3">
      <ToastContainer position="bottom-right" autoClose={2500} hideProgressBar newestOnTop />

      {/* Header */}
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            Live Delivery Ops
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Track every parcel booking in real time — locations, riders and status.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoRefresh((v) => !v)}
            className={`flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium transition ${
              autoRefresh
                ? "border-green-200 bg-green-50 text-green-700"
                : "border-gray-200 text-gray-600 dark:border-white/10 dark:text-gray-300"
            }`}
          >
            <MdFiberManualRecord className={`h-3 w-3 ${autoRefresh ? "animate-pulse text-green-500" : "text-gray-400"}`} />
            {autoRefresh ? "Live" : "Paused"}
          </button>
          <button
            onClick={load}
            className="flex items-center gap-1 rounded-xl border border-gray-200 px-3 py-2 text-sm font-medium text-gray-600 transition hover:bg-gray-50 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
          >
            <MdRefresh className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Reload
          </button>
        </div>
      </div>

      {/* Status breakdown chips */}
      <div className="mb-4 flex flex-wrap gap-2">
        <Chip
          active={!statusFilter}
          tone="gray"
          label="All"
          count={allBreakdown}
          onClick={() => setStatusFilter("")}
        />
        {(data.breakdown || [])
          .slice()
          .sort((a, b) => b.count - a.count)
          .map((b) => (
            <Chip
              key={b._id}
              active={statusFilter === b._id}
              tone={toneOf(b._id)}
              label={b._id}
              count={b.count}
              onClick={() => setStatusFilter(statusFilter === b._id ? "" : b._id)}
            />
          ))}
      </div>

      <Card extra="p-5">
        {/* Toolbar */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-full max-w-xs">
            <MdSearch className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search token, pharmacy, customer, rider…"
              className="w-full rounded-xl border border-gray-200 bg-white/0 py-2.5 pl-10 pr-3 text-sm text-navy-700 outline-none transition focus:border-brand-500 dark:border-white/10 dark:text-white"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm text-gray-500">
              Status
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-white/10 dark:bg-navy-800"
              >
                <option value="">All</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>{prettyStatus(s)}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-500">
              Rows
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm dark:border-white/10 dark:bg-navy-800"
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-3">Token</th>
                <th className="pr-3">Pharmacy</th>
                <th className="pr-3">Customer</th>
                <th className="pr-3">Rider</th>
                <th className="pr-3">Status</th>
                <th className="pr-3">Fee</th>
                <th className="pr-3">Updated</th>
                <th className="pr-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b) => (
                <tr key={b._id} className="border-t border-gray-100 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5">
                  <td className="py-3 pr-3 font-mono text-xs font-semibold text-navy-700 dark:text-white">
                    {b.shortToken}
                  </td>
                  <td className="pr-3">
                    <div className="text-navy-700 dark:text-white">{b.pharmacyName || "—"}</div>
                    <div className="text-xs text-gray-400">{b.pharmacyId}</div>
                  </td>
                  <td className="pr-3">
                    <div className="text-navy-700 dark:text-white">{b.customerName || "—"}</div>
                    <div className="text-xs text-gray-400">{b.customerMobile}</div>
                  </td>
                  <td className="pr-3 text-navy-700 dark:text-white">{b.riderName || "—"}</td>
                  <td className="pr-3"><StatusPill s={b.status} /></td>
                  <td className="pr-3 text-navy-700 dark:text-white">₹{b.fee?.total || 0}</td>
                  <td className="pr-3 text-xs text-gray-500">
                    {b.updatedAt ? new Date(b.updatedAt).toLocaleString() : "—"}
                  </td>
                  <td className="pr-1">
                    <div className="flex items-center justify-end gap-1">
                      <IconBtn title="View details" onClick={() => setSelected({ id: b._id, tab: "details" })}>
                        <MdVisibility className="h-4 w-4" />
                      </IconBtn>
                      <IconBtn title="View on map" onClick={() => setSelected({ id: b._id, tab: "map" })}>
                        <MdMap className="h-4 w-4" />
                      </IconBtn>
                      <IconBtn
                        title="Delete booking"
                        danger
                        loading={deletingId === b._id}
                        onClick={() => remove(b)}
                      >
                        <MdDeleteOutline className="h-4 w-4" />
                      </IconBtn>
                    </div>
                  </td>
                </tr>
              ))}

              {rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-gray-500">
                    {loading ? "Loading…" : search ? "No bookings match your search." : "No bookings yet."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-4 text-sm dark:border-white/10">
          <span className="text-gray-500">
            {from}–{to} of <b className="text-navy-700 dark:text-white">{total}</b>
            {search && ` · ${rows.length} shown on page`}
          </span>
          <div className="flex items-center gap-1">
            <PagerBtn disabled={page <= 1} onClick={() => setPage(1)}>«</PagerBtn>
            <PagerBtn disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              <MdChevronLeft className="h-4 w-4" />
            </PagerBtn>
            <span className="px-3 text-gray-600 dark:text-gray-300">
              Page <b className="text-navy-700 dark:text-white">{page}</b> / {totalPages}
            </span>
            <PagerBtn disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              <MdChevronRight className="h-4 w-4" />
            </PagerBtn>
            <PagerBtn disabled={page >= totalPages} onClick={() => setPage(totalPages)}>»</PagerBtn>
          </div>
        </div>
      </Card>

      {selected && (
        <BookingModal
          id={selected.id}
          initialTab={selected.tab}
          onClose={() => setSelected(null)}
          onDelete={remove}
          deleting={Boolean(deletingId)}
        />
      )}
    </div>
  );
}

function Chip({ active, tone, label, count, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition ${
        active ? `${TONE_CLASS[tone]} ring-2 ring-brand-400` : `${TONE_CLASS[tone]} opacity-80 hover:opacity-100`
      }`}
    >
      <MdFiberManualRecord className={`h-2.5 w-2.5 ${DOT_CLASS[tone]}`} />
      {prettyStatus(label)} <b>{count}</b>
    </button>
  );
}

function StatusPill({ s }) {
  const tone = toneOf(s);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${TONE_CLASS[tone]}`}>
      <MdFiberManualRecord className={`h-2 w-2 ${DOT_CLASS[tone]}`} />
      {prettyStatus(s)}
    </span>
  );
}

function IconBtn({ children, title, onClick, danger, loading }) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={loading}
      className={`rounded-lg p-1.5 transition disabled:opacity-50 ${
        danger
          ? "text-gray-500 hover:bg-red-50 hover:text-red-500 dark:hover:bg-white/10"
          : "text-gray-500 hover:bg-gray-100 hover:text-brand-600 dark:hover:bg-white/10"
      }`}
    >
      {loading ? <MdRefresh className="h-4 w-4 animate-spin" /> : children}
    </button>
  );
}

function PagerBtn({ children, disabled, onClick }) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className="flex h-8 min-w-8 items-center justify-center rounded-lg border border-gray-200 px-2 text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-gray-300 dark:hover:bg-white/5"
    >
      {children}
    </button>
  );
}

function BookingModal({ id, initialTab, onClose, onDelete, deleting }) {
  const [tab, setTab] = useState(initialTab || "details");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const res = await ldAdminApi.bookingDetail(id);
        if (live) setData(res);
      } catch (e) {
        toast.error(e.response?.data?.message || e.message);
        onClose();
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const booking = data?.booking;
  const locations = data?.locations || {};
  const notifications = data?.notifications || [];

  const TABS = [
    { k: "details", label: "Details" },
    { k: "map", label: "Map" },
    { k: "notifications", label: `Notifications (${notifications.length})` },
  ];

  const tone = booking ? toneOf(booking.status) : "gray";

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/60 p-4 backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl dark:bg-navy-800"
      >
        {/* Hero header */}
        <div className={`relative px-6 pb-5 pt-6 ${HERO_BG[tone]}`}>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full bg-white/70 p-1.5 text-gray-600 backdrop-blur transition hover:bg-white dark:bg-white/10 dark:text-white"
          >
            <MdClose className="h-5 w-5" />
          </button>

          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-gray-500">
            <MdReceiptLong className="h-4 w-4" /> Booking
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <span className="font-mono text-2xl font-bold text-navy-700 dark:text-white">
              {loading ? "…" : booking?.shortToken}
            </span>
            {booking && <StatusPill s={booking.status} />}
          </div>
          {booking && (
            <div className="mt-1 text-xs text-gray-500">
              Created {booking.createdAt ? new Date(booking.createdAt).toLocaleString() : "—"}
              {" · "}Updated {booking.updatedAt ? new Date(booking.updatedAt).toLocaleString() : "—"}
            </div>
          )}

          {/* Quick stats */}
          {booking && (
            <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
              <HeroStat icon={MdReceiptLong} label="Fee" value={`₹${booking.fee?.total || 0}`} />
              <HeroStat icon={MdMap} label="Distance" value={`${booking.distanceKm || 0} km`} />
              <HeroStat icon={MdInventory2} label="Package" value={booking.packageSize || "—"} />
              <HeroStat icon={MdPayments} label="Payment" value={booking.payment?.status || "—"} />
            </div>
          )}
        </div>

        {/* tabs */}
        <div className="flex items-center justify-between border-b border-gray-100 px-4 dark:border-white/10">
          <div className="flex gap-1">
            {TABS.map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`-mb-px border-b-2 px-4 py-3 text-sm font-medium transition ${
                  tab === t.k
                    ? "border-brand-500 text-brand-600 dark:text-white"
                    : "border-transparent text-gray-500 hover:text-navy-700 dark:hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {booking && (
            <button
              onClick={() => onDelete(booking)}
              disabled={deleting}
              className="flex items-center gap-1 rounded-lg px-3 py-1.5 text-sm font-medium text-red-500 transition hover:bg-red-50 disabled:opacity-50 dark:hover:bg-white/10"
            >
              <MdDeleteOutline className="h-4 w-4" /> Delete
            </button>
          )}
        </div>

        {/* body */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading && (
            <div className="space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-20 animate-pulse rounded-xl bg-gray-100 dark:bg-white/5" />
              ))}
            </div>
          )}

          {!loading && booking && tab === "details" && <DetailsTab booking={booking} />}
          {!loading && booking && tab === "map" && <MapTab locations={locations} booking={booking} />}
          {!loading && booking && tab === "notifications" && <NotificationsTab notifications={notifications} />}
        </div>
      </div>
    </div>
  );
}

function HeroStat({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2 rounded-xl bg-white/70 px-3 py-2 backdrop-blur dark:bg-white/10">
      <Icon className="h-4 w-4 shrink-0 text-brand-500" />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wide text-gray-500">{label}</div>
        <div className="truncate text-sm font-semibold capitalize text-navy-700 dark:text-white">{value}</div>
      </div>
    </div>
  );
}

function DetailsTab({ booking }) {
  return (
    <div className="space-y-4">
      <InfoCard icon={MdLocalPharmacy} title="Pharmacy" tone="green">
        <Info label="Name" value={booking.pharmacyName || "—"} />
        <Info label="ID" value={booking.pharmacyId} mono />
        <Info label="Phone" value={booking.pharmacyPhone || "—"} />
        <Info label="Order value" value={`₹${booking.orderValue || 0}`} />
      </InfoCard>

      <InfoCard icon={MdPerson} title="Customer" tone="red">
        <Info label="Name" value={booking.customerName || "—"} />
        <Info label="Phone" value={booking.customerMobile} />
        {booking.deliveryAddress?.fullText && (
          <Info label="Address" value={booking.deliveryAddress.fullText} wide />
        )}
      </InfoCard>

      <InfoCard icon={MdTwoWheeler} title="Delivery partner" tone="blue">
        {booking.riderName ? (
          <>
            <Info label="Name" value={booking.riderName} />
            <Info label="Phone" value={booking.riderPhone || "—"} />
            <Info label="Vehicle" value={booking.riderVehicleNo || "—"} />
          </>
        ) : (
          <div className="col-span-full text-sm text-gray-400">No rider assigned yet.</div>
        )}
      </InfoCard>

      <InfoCard icon={MdPayments} title="Payment & fee" tone="gray">
        <Info label="Method" value={booking.payment?.method || "—"} />
        <Info label="Status" value={booking.payment?.status || "—"} />
        <Info label="Amount" value={`₹${booking.payment?.amount || 0}`} />
        <Info label="Fee total" value={`₹${booking.fee?.total || 0}`} />
      </InfoCard>

      {/* Timeline */}
      <div>
        <h4 className="mb-3 text-sm font-semibold text-navy-700 dark:text-white">Status timeline</h4>
        <ol className="space-y-3 border-l-2 border-gray-100 pl-5 dark:border-white/10">
          {(booking.statusHistory || []).map((h, i) => {
            const t = toneOf(h.status);
            return (
              <li key={i} className="relative">
                <span className={`absolute -left-[26px] top-0.5 h-3.5 w-3.5 rounded-full ring-4 ring-white dark:ring-navy-800 ${DOT_BG[t]}`} />
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-navy-700 dark:text-white">{prettyStatus(h.status)}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${TONE_CLASS[t]}`}>{h.by}</span>
                </div>
                <div className="text-xs text-gray-400">
                  {h.at ? new Date(h.at).toLocaleString() : ""}
                  {h.reason ? ` · ${h.reason}` : ""}
                </div>
              </li>
            );
          })}
          {(!booking.statusHistory || booking.statusHistory.length === 0) && (
            <li className="text-xs text-gray-400">No history.</li>
          )}
        </ol>
      </div>
    </div>
  );
}

function InfoCard({ icon: Icon, title, tone, children }) {
  return (
    <div className="rounded-2xl border border-gray-100 p-4 dark:border-white/10">
      <div className="mb-3 flex items-center gap-2">
        <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${TONE_CLASS[tone]}`}>
          <Icon className="h-4 w-4" />
        </span>
        <h4 className="text-sm font-semibold text-navy-700 dark:text-white">{title}</h4>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3">{children}</div>
    </div>
  );
}

function MapTab({ locations, booking }) {
  const points = [
    locations.pharmacy && {
      ...locations.pharmacy, key: "pharmacy", label: "Pharmacy",
      sub: booking.pharmacyName, color: "green",
      url: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
    },
    locations.customer && {
      ...locations.customer, key: "customer", label: "Customer",
      sub: booking.customerName || booking.customerMobile, color: "red",
      url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
    },
    locations.rider && {
      ...locations.rider, key: "rider", label: "Delivery partner",
      sub: booking.riderName, color: "blue",
      url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png",
    },
  ].filter(Boolean);

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-4 text-sm">
        <Legend icon={MdLocalPharmacy} color="text-green-600" label="Pharmacy" on={!!locations.pharmacy} />
        <Legend icon={MdPerson} color="text-red-500" label="Customer" on={!!locations.customer} />
        <Legend icon={MdTwoWheeler} color="text-blue-600" label="Delivery partner" on={!!locations.rider} />
      </div>

      {points.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded-xl border border-dashed border-gray-300 text-sm text-gray-400 dark:border-white/10">
          No location data captured for this booking yet.
        </div>
      ) : (
        <LocationsMap points={points} />
      )}

      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
        {points.map((p) => (
          <div key={p.key} className="rounded-xl border border-gray-200 p-3 text-xs dark:border-white/10">
            <div className={`font-semibold ${p.color === "green" ? "text-green-600" : p.color === "red" ? "text-red-500" : "text-blue-600"}`}>
              {p.label}
            </div>
            <div className="text-navy-700 dark:text-white">{p.sub || "—"}</div>
            <div className="mt-1 font-mono text-[11px] text-gray-400">
              {p.lat.toFixed(5)}, {p.lng.toFixed(5)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function LocationsMap({ points }) {
  const [active, setActive] = useState(null);
  const center = points[0];

  const onLoad = (map) => {
    if (points.length > 1 && window.google) {
      const bounds = new window.google.maps.LatLngBounds();
      points.forEach((p) => bounds.extend({ lat: p.lat, lng: p.lng }));
      map.fitBounds(bounds, 64);
    }
  };

  return (
    <LoadScript googleMapsApiKey={GOOGLE_KEY}>
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "340px", borderRadius: "16px" }}
        center={{ lat: center.lat, lng: center.lng }}
        zoom={14}
        onLoad={onLoad}
        options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
      >
        {points.map((p) => (
          <Marker
            key={p.key}
            position={{ lat: p.lat, lng: p.lng }}
            icon={{ url: p.url }}
            title={p.label}
            onClick={() => setActive(p.key)}
          >
            {active === p.key && (
              <InfoWindow onCloseClick={() => setActive(null)}>
                <div style={{ minWidth: 120 }}>
                  <strong>{p.label}</strong>
                  <div>{p.sub || ""}</div>
                </div>
              </InfoWindow>
            )}
          </Marker>
        ))}
      </GoogleMap>
    </LoadScript>
  );
}

function NotificationsTab({ notifications }) {
  if (!notifications.length) {
    return <div className="py-12 text-center text-sm text-gray-400">No notifications logged.</div>;
  }
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left uppercase tracking-wide text-gray-500">
          <th className="py-2 pr-3">Channel</th>
          <th className="pr-3">Event</th>
          <th className="pr-3">To</th>
          <th className="pr-3">Status</th>
          <th className="pr-3">When</th>
        </tr>
      </thead>
      <tbody>
        {notifications.map((n) => (
          <tr key={n._id} className="border-t border-gray-100 dark:border-white/10">
            <td className="py-2 pr-3 text-navy-700 dark:text-white">{n.channel}</td>
            <td className="pr-3">{n.eventKey}</td>
            <td className="pr-3">{n.to || "—"}</td>
            <td className="pr-3">{n.status}</td>
            <td className="pr-3 text-gray-400">
              {n.createdAt ? new Date(n.createdAt).toLocaleString() : "—"}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Legend({ icon: Icon, color, label, on }) {
  return (
    <span className={`flex items-center gap-1.5 ${on ? "" : "opacity-40"}`}>
      <Icon className={`h-4 w-4 ${color}`} />
      <span className="text-gray-600 dark:text-gray-300">{label}</span>
      {!on && <span className="text-[10px] text-gray-400">(no data)</span>}
    </span>
  );
}

function Info({ label, value, mono, wide }) {
  return (
    <div className={wide ? "col-span-2 sm:col-span-3" : ""}>
      <div className="text-xs text-gray-500">{label}</div>
      <div className={`font-medium text-navy-700 dark:text-white ${mono ? "font-mono text-xs" : ""}`}>
        {value}
      </div>
    </div>
  );
}
